import {
  FieldSet,
  FormValues,
  getValue,
  InputFormGroup,
  InputLink,
  InputWizardPanelComponent,
  SelectComponent,
  TextInput,
} from "o4a-react";
import * as React from "react";
import * as Messages from "../../codegen/Messages";
import { RegionSelect } from "../../components/RegionSelect/RegionSelect";
import {
  Fields as CreateResourceFields,
  ResourceGroupNewPanel,
} from "../../components/ResourceGroupNewPanel/ResourceGroupNewPanel";
import { ResourceGroupSelect } from "../../components/ResourceGroupSelect/ResourceGroupSelect";
import { SubscriptionSelect } from "../../components/SubscriptionSelect/SubscriptionSelect";
import { nameCharMaxLength, nameCharMinLength, nameValidationRegex } from "../../helpers/validationHelper";

export const GROUP_BASICS = "basics";

const enum NewSidePanels {
  RESOURCE_GROUP = "RESOURCE_GROUP",
}

export const enum Fields {
  Subscription = "subscriptionId",
  ResourceGroup = "resourceGroupId",
  Name = "cloudVmClusterDetails.name",
  Region = "region",
}
export interface BasicsTabComponent {
  closeSidePanels: () => void;
}
export interface BasicsTabContentProps {
  location?: string;
  subscription?: string;
  resourceGroupId?: string;
  disableUIControls?: Fields[] | undefined;
  inputWizardPanelRef: InputWizardPanelComponent;
  componentRef?: (component: BasicsTabComponent) => void;
}

export const BasicsTabContent = (
  {
    location, subscription, resourceGroupId, disableUIControls,
    inputWizardPanelRef, componentRef,
  }: BasicsTabContentProps,
): JSX.Element => {
  const [subscriptionId, setSubscriptionId] = React.useState<string>(subscription || "");
  const [sidePanelOpen, setSidePanelOpen] = React.useState<NewSidePanels | undefined>(undefined);

  const closeSidePanels = (): void => setSidePanelOpen(undefined);

  React.useEffect(() => {
    if (componentRef) {
      componentRef({ closeSidePanels } as BasicsTabComponent);
    }
    // Only at mount time. Others props should not have side effect beyond their initial values
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmitNewResource = (formValues: FormValues): void => {
    const resourceName = getValue(formValues, CreateResourceFields.Name, InputFormGroup);
    resourceSelectRef.current?.setOptionNew(
      resourceName,
      { displayName: resourceName },
    );
    setSidePanelOpen(undefined);
  };

  const resourceSelectRef = React.useRef<SelectComponent>();
  const setResourceSelectRef = (selectComponent: SelectComponent): void => {
    resourceSelectRef.current = selectComponent;
  };

  function createResourceLink(
    createNewLabel: string,
    sidePanelId: NewSidePanels,
  ): InputLink {
    return {
      id: "create_new_resource",
      text: createNewLabel,
      onLinkClick: () => setSidePanelOpen(sidePanelId),
    };
  }

  const newResourceMeta = createResourceLink(Messages.actions.createNew(), NewSidePanels.RESOURCE_GROUP);

  const onSubscriptionChange = (subId: string): void => setSubscriptionId(subId);

  const onResourceGroupError = (): void => {
    inputWizardPanelRef.showError(Messages.createCommon.loadingErrors.resourceGroups());
  };

  const onResourceGroupMissingDependencies = (): void => inputWizardPanelRef.showErrorDialog(
    Messages.createCommon.dependencyWarnings.subscriptionNotSelected.message(),
    Messages.createCommon.dependencyWarnings.subscriptionNotSelected.title(),
  );

  return (
    <>
      <FieldSet header={Messages.createInfra.basicsTab.sectionTitles.projectDetails()}>
        <SubscriptionSelect
          required
          onChange={onSubscriptionChange}
          fieldName={Fields.Subscription}
          label={Messages.labels.subscription()}
          tooltip={Messages.hints.toolTipSubscription()}
          disabled={disableUIControls?.find(ele => ele === Fields.Subscription) !== undefined}
          defaultValue={subscriptionId ? [subscriptionId] : undefined}
        />
        <ResourceGroupSelect
          required
          subField
          subscriptionId={subscriptionId}
          componentRef={setResourceSelectRef}
          fieldName={Fields.ResourceGroup}
          label={Messages.labels.resourceGroup()}
          tooltip={Messages.hints.toolTipResourceGroup()}
          onError={onResourceGroupError}
          onMissingDependencies={onResourceGroupMissingDependencies}
          defaultValue={resourceGroupId ? [resourceGroupId] : undefined}
          disabled={disableUIControls?.find(ele => ele === Fields.ResourceGroup) !== undefined}
        />
      </FieldSet>
      <FieldSet header={Messages.createInfra.basicsTab.sectionTitles.instanceDetails()}>
        <TextInput
          required
          fieldName={Fields.Name}
          label={Messages.labels.name()}
          validator={(value: string) => {
            const errors: string[] = [];
            if (!value || (value?.length < nameCharMinLength || value?.length > nameCharMaxLength)) {
              errors.push(Messages.validation.nameChars(nameCharMinLength.toString(), nameCharMaxLength.toString()));
            }
            if (!value || (value && !nameValidationRegex.test(value))) {
              errors.push(Messages.validation.nameValidation());
            }
            return errors.length > 0 ? errors : undefined;
          }}
        />
        <RegionSelect
          required
          subscriptionId={subscriptionId}
          // showUnlinkedRegions
          fieldName={Fields.Region}
          label={Messages.labels.region()}
          tooltip={Messages.hints.toolTipRegion()}
          defaultValue={location ? [location] : undefined}
          disabled={disableUIControls?.find(ele => ele === Fields.Region) !== undefined}
        />
      </FieldSet>
      {sidePanelOpen === NewSidePanels.RESOURCE_GROUP
        && (
          <ResourceGroupNewPanel
            defaultValue={resourceSelectRef.current?.getOptionNew()}
            targetId={newResourceMeta.id}
            onSubmit={onSubmitNewResource}
            onClose={() => setSidePanelOpen(undefined)}
            description={Messages.createNewPanels.resourceGroup.description()}
          />
        )}
    </>
  );
};
